import CustomSelect from '~/components/Shared/CustomSelect/CustomSelect';
import { InputBar } from '~/ui';
import { useFormik } from 'formik';
import { IUsersFilters } from '~/utils/types/userFilterTypes';
import dayjs, { Dayjs } from 'dayjs';
import { t } from 'i18next';
import styles from './OrderFilter.module.scss';
import { validate } from '~/utils/validateOrderFilters';
import { useEffect, useState } from 'react';
import { FilterButtons } from '../FiltersButtons/FilterButtons';
import { useQuery } from '@tanstack/react-query';
import { instance } from '~/utils/api/api';
import { useToastError } from '~/utils/useToastError';
import { getCompaniesModuleOptions } from '~/utils/getCompaniesModuleOptions';
import { CustomDatePicker } from '~/components/Shared/CustomDatePicker/CustomDatePicker';

interface IFormik {
  dates: {
    start_date: Dayjs | null;
    end_date: Dayjs | null;
  };
  cancel_orders: {
    cancel_by: { id: number; name: string; display_name: string };
    condition: { id: number; name: string; display_name: string };
    count: string;
  };
  cancel_wares: {
    condition: { id: number; name: string; display_name: string };
    count: string;
  };
  announcement_organizations: { id: number; name: string; full_name: string }[];
}

interface IOrderProps {
  selectedUsers: number[];
  onClick: (filters: IUsersFilters) => void;
  setIsStatusUsersModalOpen: () => void;
  isUsersLoading: boolean;
}

export const OrderFilter = ({
  onClick,
  selectedUsers,
  setIsStatusUsersModalOpen,
  isUsersLoading,
}: IOrderProps) => {
  const [isAnyFieldFilled, setIsAnyFieldFilled] = useState(false);
  const toastError = useToastError();

  const initialValues = {
    dates: {
      start_date: null,
      end_date: null,
    },
    cancel_orders: {
      cancel_by: { id: 0, name: '', display_name: '' },
      condition: { id: 0, name: '', display_name: '' },
      count: '',
    },
    cancel_wares: {
      condition: { id: 0, name: '', display_name: '' },
      count: '',
    },
    announcement_organizations: [],
  };

  const { data: companies } = useQuery({
    queryFn: async () => {
      const response = await instance.get('admin/user-company');
      return response.data;
    },
    queryKey: ['user-company'],
    onError: error => {
      toastError(error);
    },
  });

  const companyModules = getCompaniesModuleOptions(companies?.companies);

  const formik = useFormik<IFormik>({
    initialValues,
    validate,
    onSubmit: values => {
      const company_name = values.announcement_organizations.map(
        item => item.name
      );
      const filters = {
        ...(values.cancel_orders.condition.name &&
          values.cancel_orders.count &&
          values.cancel_orders.cancel_by.name && {
            cancel_orders: {
              condition: values.cancel_orders.condition.name,
              cancel_by: values.cancel_orders.cancel_by.name,
              count: values.cancel_orders.count,
            },
          }),
        ...(values.cancel_wares.condition.name &&
          values.cancel_wares.count && {
            cancel_wares: {
              condition: values.cancel_wares.condition.name,
              count: values.cancel_wares.count,
            },
          }),
        ...(values.dates.end_date &&
          values.dates.start_date && {
            order_created_dates: {
              dates: [
                dayjs(values.dates.start_date)
                  .startOf('day')
                  .format('YYYY-MM-DD HH:mm:ss'),
                dayjs(values.dates.end_date)
                  .endOf('day')
                  .format('YYYY-MM-DD HH:mm:ss'),
              ],
            },
          }),
        ...(values.announcement_organizations.length !== 0
          ? {
              companies: {
                names: company_name,
              },
            }
          : {}),
      };
      onClick(filters);
    },
  });

  const handleResetForm = () => {
    formik.resetForm();
    onClick({});
  };

  useEffect(() => {
    const isFilled =
      !!formik.values.dates.end_date ||
      !!formik.values.dates.start_date ||
      !!formik.values.cancel_orders.cancel_by.name ||
      !!formik.values.cancel_orders.condition.name ||
      !!formik.values.cancel_orders.count ||
      !!formik.values.cancel_wares.condition.name ||
      !!formik.values.cancel_wares.count ||
      !!formik.values.announcement_organizations.length;

    setIsAnyFieldFilled(isFilled);
  }, [formik.values]);

  return (
    <div className={styles.orderContainer}>
      <form onSubmit={formik.handleSubmit} className={styles.form}>
        <div className={styles.inputsWrapper}>
          <div className={styles.filtersLeft}>
            <div className={styles.dateWrapper}>
              <CustomDatePicker
                title={t('start_n_end_date_order_period')}
                value={[
                  formik.values.dates.start_date,
                  formik.values.dates.end_date,
                ]}
                onChange={(name, value) => {
                  if (name === 'start_date') {
                    formik.setFieldValue('dates.start_date', value);
                  } else {
                    formik.setFieldValue('dates.end_date', value);
                  }
                }}
              />
            </div>
            <div className={styles.companyWapper}>
              <CustomSelect
                label={t('company')}
                options={companyModules}
                value={formik.values.announcement_organizations}
                onChange={value => {
                  formik.setFieldValue('announcement_organizations', value);
                }}
                placeholder={t('validate_organizations')}
                disabled={isUsersLoading}
                multiselect
                className={styles.companySelect}
              />
              <CustomSelect
                label={t('cancellation_reason')}
                value={formik.values.cancel_orders.cancel_by}
                className={styles.topSelect}
                options={[
                  {
                    id: 1,
                    display_name: `${t('cancelled_by_user')}`,
                    name: 'admin',
                  },
                  {
                    id: 2,
                    display_name: `${t('cancelled_by_system')}`,
                    name: 'system',
                  },
                  {
                    id: 3,
                    display_name: `${t('cancelled_by_administrator')}`,
                    name: 'admin',
                  },
                ]}
                placeholder={t('select_cancellation_reason')}
                onChange={value =>
                  formik.setFieldValue('cancel_orders.cancel_by', value)
                }
                onTouch={() =>
                  formik.setFieldTouched('cancel_orders.cancel_by', true)
                }
                error={formik.errors.cancel_orders?.cancel_by?.name}
              />
            </div>
          </div>
          <div className={styles.operators}>
            <div className={styles.operatorWrapper}>
              <span className={styles.operatorsLabel}>
                {t('number_of_cancelled_dishes')}
              </span>
              <div className={styles.operatorLine}>
                <CustomSelect
                  value={formik.values.cancel_wares.condition}
                  options={[
                    { id: 1, display_name: `${t('more')}`, name: '>' },
                    { id: 2, display_name: `${t('less')}`, name: '<' },
                    { id: 3, display_name: `${t('equal')}`, name: '=' },
                  ]}
                  placeholder={t('select_operator')}
                  onChange={value =>
                    formik.setFieldValue('cancel_wares.condition', value)
                  }
                  error={formik.errors.cancel_wares?.condition?.name}
                  className={styles.condition}
                />
                <InputBar
                  value={formik.values.cancel_wares.count}
                  name="cancel_wares.count"
                  onChange={formik.handleChange}
                  classNameInputWrapper={styles.input}
                  placeholder={t('enter_the_number')}
                  label=""
                  errors={formik.errors.cancel_wares?.count}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.cancel_wares?.count &&
                    Boolean(formik.errors.cancel_wares?.count)
                  }
                />
              </div>
            </div>
            <div className={styles.operatorWrapper}>
              <span className={styles.operatorsLabel}>
                {t('number_of_cancelled_orders')}
              </span>
              <div className={styles.operatorLine}>
                <CustomSelect
                  value={formik.values.cancel_orders.condition}
                  options={[
                    { id: 1, display_name: `${t('more')}`, name: '>' },
                    { id: 2, display_name: `${t('less')}`, name: '<' },
                    { id: 3, display_name: `${t('equal')}`, name: '=' },
                  ]}
                  placeholder={t('select_operator')}
                  onChange={value =>
                    formik.setFieldValue('cancel_orders.condition', value)
                  }
                  onTouch={() =>
                    formik.setFieldTouched(
                      'values.cancel_orders.condition.name',
                      true
                    )
                  }
                  error={formik.errors.cancel_orders?.condition?.name}
                  className={styles.condition}
                />
                <InputBar
                  value={formik.values.cancel_orders.count}
                  name="cancel_orders.count"
                  onChange={formik.handleChange}
                  classNameInputWrapper={styles.input}
                  placeholder={t('enter_the_number')}
                  label=""
                  errors={formik.errors.cancel_orders?.count}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.cancel_orders?.count &&
                    Boolean(formik.errors.cancel_orders?.count)
                  }
                />
              </div>
            </div>
          </div>
        </div>
        <FilterButtons
          isAnyFieldFilled={isAnyFieldFilled}
          selectedUsers={selectedUsers}
          resetForm={handleResetForm}
          setIsModalOpen={setIsStatusUsersModalOpen}
          isLoading={isUsersLoading}
        />
      </form>
    </div>
  );
};
